"use client";

import React, { useEffect, useRef, useState } from "react";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AnimatedCounter({
  value,
  suffix = "",
  prefix = "",
  decimals = 0,
  isVisible
}: {
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  isVisible: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const end = value;
    const duration = 1500;
    const frameRate = 1000 / 60;
    const totalFrames = Math.round(duration / frameRate);
    const increment = end / totalFrames;
    let currentFrame = 0;
    const timer = setInterval(() => {
      currentFrame++;
      start += increment;
      const progress = currentFrame / totalFrames;
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = end * easeOutQuart;
      if (currentFrame >= totalFrames) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(currentCount);
      }
    }, frameRate);
    return () => clearInterval(timer);
  }, [isVisible, value]);
  const formattedCount = decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toLocaleString('en-US');
  return <span className="font-mono tabular-nums tracking-tighter">
            {prefix}{formattedCount}{suffix}
        </span>;
}
const generateSparklineData = (base: number, variance: number, trend: number) => {
  return Array.from({
    length: 30
  }, (_, i) => ({
    day: `Day ${i + 1}`,
    value: Math.max(0, base + (Math.random() * variance - variance / 2) + i * trend)
  }));
};
const marketStats = [{
  id: "experience",
  label: "Years Experience",
  value: 40,
  prefix: "",
  suffix: "+",
  decimals: 0,
  data: generateSparklineData(35, 5, 0.2)
}, {
  id: "properties",
  label: "Properties Engineered",
  value: 300,
  prefix: "",
  suffix: "+",
  decimals: 0,
  data: generateSparklineData(250, 40, 1.5)
}, {
  id: "licenses",
  label: "State Licenses",
  value: 2,
  prefix: "",
  suffix: "",
  decimals: 0,
  data: generateSparklineData(2, 0.5, 0.05)
}];
const tickerItems = ["CALIFORNIA LICENSED P.E.", "UTAH STRUCTURAL ENGINEER", "CA BOARD EXPERT WITNESS", "FOUNDATION CORRECTION SPECIALIST", "STRUCTURAL INTEGRITY VERIFIED", "ALPINE ENGINEERING LLC", "ESTABLISHED PROFESSIONAL"];
export default function RealTimeMarketPulse() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const tickerText = [...tickerItems, ...tickerItems, ...tickerItems].join(" • ");
  return <section data-section-id="222920" id="proven-impact-by-the-numbers" className="bg-background text-foreground relative overflow-hidden flex flex-col border-y border-border">
            <style dangerouslySetInnerHTML={{
      __html: `
                @keyframes ticker-scroll {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-33.33%); }
                }
                .animate-ticker-scroll {
                    animation: ticker-scroll 40s linear infinite;
                    white-space: nowrap;
                    will-change: transform;
                }
                `
    }} />

            {}
            <div className="bg-primary text-primary-foreground py-2 border-b border-border/10 overflow-hidden flex items-center">
                <div className="flex animate-ticker-scroll font-mono text-xs md:text-sm font-medium tracking-widest uppercase opacity-80">
                    <span className="px-4">{tickerText}</span>
                    <span className="px-4">{tickerText}</span>
                    <span className="px-4">{tickerText}</span>
                </div>
            </div>

            {}
            <div ref={sectionRef} className="bg-secondary w-full py-12 md:py-16 lg:py-20">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0 divide-y md:divide-y-0 md:divide-x divide-border">
                        {marketStats.map((stat, i) => <div key={stat.id} data-index={i} className={cn("flex flex-col px-4 md:px-8 lg:px-12 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                                <div className="w-12 h-1 bg-ring mb-6" />
                                <h3 className="font-mono text-muted-foreground text-xs md:text-sm font-medium uppercase tracking-widest mb-2">
                                    {stat.label}
                                </h3>
                                
                                <div className="text-5xl md:text-6xl lg:text-7xl font-default font-bold text-foreground mb-6 tracking-tighter">
                                    <AnimatedCounter value={stat.value} prefix={stat.prefix} suffix={stat.suffix} decimals={stat.decimals} isVisible={isVisible} />
                                </div>

                                <div className="h-16 md:h-20 w-full mt-auto opacity-40 grayscale">
                                    <ChartContainer config={{
                value: {
                  label: stat.label,
                  color: "var(--primary)"
                }
              }} className="h-full w-full">
                                        <LineChart data={stat.data} margin={{
                  top: 5,
                  right: 5,
                  bottom: 5,
                  left: 5
                }}>
                                            <ChartTooltip content={<ChartTooltipContent hideLabel indicator="line" />} cursor={{
                    stroke: 'var(--border)',
                    strokeWidth: 1,
                    strokeDasharray: '4 4'
                  }} />
                                            <Line type="stepAfter" dataKey="value" stroke="var(--color-value)" strokeWidth={1.5} dot={false} activeDot={{
                    r: 3,
                    fill: "var(--background)",
                    stroke: "var(--color-value)",
                    strokeWidth: 2
                  }} isAnimationActive={isVisible} animationDuration={1500} animationEasing="ease-out" />
                                        </LineChart>
                                    </ChartContainer>
                                </div>
                                <div className="flex justify-between items-center mt-3 text-[10px] font-mono text-muted-foreground/60 tracking-widest">
                                    <span>HISTORICAL DATA</span>
                                    <span className="text-primary flex items-center gap-1">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>
                                        VERIFIED
                                    </span>
                                </div>
                            </div>)}
                    </div>
                </div>
            </div>
        </section>;
}